import { api } from "./api";

export interface ColumnQuestion {
  column_name: string;
  question: string;
  questions?: string[];
}

export interface ExtractionInput {
  sourcefile: string;
  columns: ColumnQuestion[];
  priming: string;
  document_id: string;
  model?: string;
}

export interface ExtractionColumnValue {
  column_name: string;
  column_value: string;
}

export interface ExtractionRow {
  population: string;
  columns: ExtractionColumnValue[];
}

export interface ExtractionResult {
  file_name: string;
  document_id: string;
  rows: ExtractionRow[];
}

// ── Service Bus queue-based extraction ──────────────────────────────────────

export interface EnqueueDocumentInput {
  document_id: string;
  sourcefile: string;
  columns: ColumnQuestion[];
  priming: string;
  model?: string;
}

export interface EnqueueExtractionRequest {
  template_id: string;
  documents: EnqueueDocumentInput[];
}

export interface DocumentStatus {
  status: string;
  queue_position?: number;
}

export interface ExtractionStatusResponse {
  statuses: Record<string, DocumentStatus>;
  all_done: boolean;
  queue_size: number;
}

export interface ReviewColumnInput {
  column_name: string;
  column_value: string;
  question: string;
}

export interface ReviewInput {
  sourcefile: string;
  document_id: string;
  columns: ReviewColumnInput[];
  priming: string;
  model: string;
}

export interface ReviewColumnOutput {
  column_name: string;
  score: number;
  suggestions: string[];
  alternative_answer?: string;
}

export interface ReviewResult {
  file_name: string;
  document_id: string;
  columns: ReviewColumnOutput[];
}

export interface ChunkResponse {
  content: string;
  sourcepage: string;
}

// A document counts toward extraction progress once it has left the Queued state.
export const isCountedDocStatus = (status: string): boolean =>
  status === "Processing" || status === "Processed" || status === "Error";

export async function fetchChunk(
  sourcefile: string,
  documentId: string,
  sourceText: string
): Promise<ChunkResponse | null> {
  try {
    const result = await api.post<ChunkResponse>("/api/extraction/chunks", {
      sourcefile,
      document_id: documentId,
      source_text: sourceText,
    });
    return result ? { content: result.content ?? "", sourcepage: result.sourcepage ?? "" } : null;
  } catch {
    return null;
  }
}

export const extractionApi = {
  // Single-document sync extraction — used by per-row "Process" button
  extractData: (input: ExtractionInput): Promise<ExtractionResult[]> =>
    api.post<ExtractionResult[]>("/api/extraction/extract-data", [input]),

  // Enqueue all documents for async Service Bus processing
  enqueueExtract: (projectId: string, request: EnqueueExtractionRequest): Promise<{ enqueued: { document_id: string; sentinel_id: string }[] }> =>
    api.post(`/api/projects/${projectId}/enqueue-extract`, request),

  // Poll extraction status for a template's documents
  getExtractionStatus: (projectId: string, templateId: string): Promise<ExtractionStatusResponse> =>
    api.get(`/api/projects/${projectId}/extraction-status?template_id=${templateId}`),

  // Delete stale Queued/Processing sentinels (resets stuck extraction state)
  cancelExtraction: (projectId: string, templateId: string): Promise<{ cancelled: number }> =>
    api.delete(`/api/projects/${projectId}/extraction-sentinels?template_id=${templateId}`),

   // Review extracted data and get AI scores
  reviewData: (input: ReviewInput, signal?: AbortSignal): Promise<ReviewResult[]> =>
    api.post<ReviewResult[]>("/api/extraction/review-data", [input], signal),

  // Review using a custom extraction endpoint path (from api_configuration)
  reviewDataAtPath: (apiName: string, input: ReviewInput): Promise<ReviewResult[]> =>
    api.post<ReviewResult[]>(`/api/extraction/${apiName}`, [input]),
};
